import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  FileText,
  Download,
  BarChart3,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import {
  fetchAuditLogs,
  fetchReportDownloads,
  fetchDownloadSummary,
  fmtTimestamp,
  actionLabel,
  groupLogRows,
  tableLabel,
  fieldLabel,
  summarizeFields,
} from "../../services/audit_log";
import "./AuditLogViewer.css";


const ACTION_ICONS = {
  create: Plus,
  update: Pencil,
  delete: Trash2,
};

function actionIcon(action) {
  return ACTION_ICONS[String(action || "").toLowerCase()] || FileText;
}

export default function AuditLogViewer({ apiBaseUrl, getToken }) {
  const [tab, setTab] = useState("logs");
  const [headerVisible, setHeaderVisible] = useState(true);
  const pageRef = useRef(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    function handleScroll() {
      const currentY = el.scrollTop;
      const goingDown = currentY > lastScrollY.current;
      setHeaderVisible(!goingDown || currentY < 40);
      lastScrollY.current = currentY;
    }

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="audit-log-layout">
      <Sidebar />
      <div className="audit-log-content">
        <div
          className={`audit-log-header-wrapper ${headerVisible ? "audit-log-header-visible" : "audit-log-header-hidden"
            }`}
        >
          <Header />
        </div>

        <div className="audit-log-page-container" ref={pageRef}>
          <div className="audit-log-page-header">

            <nav className="audit-log-tabs">
              <button
                className={tab === "logs" ? "audit-log-tab active" : "audit-log-tab"}
                onClick={() => setTab("logs")}
              >
                <FileText size={15} /> Change log
              </button>
              <button
                className={tab === "downloads" ? "audit-log-tab active" : "audit-log-tab"}
                onClick={() => setTab("downloads")}
              >
                <Download size={15} /> Report downloads
              </button>
              <button
                className={tab === "summary" ? "audit-log-tab active" : "audit-log-tab"}
                onClick={() => setTab("summary")}
              >
                <BarChart3 size={15} /> Download summary
              </button>
            </nav>
          </div>

          {tab === "logs" && <LogsPanel apiBaseUrl={apiBaseUrl} getToken={getToken} />}
          {tab === "downloads" && <DownloadsPanel apiBaseUrl={apiBaseUrl} getToken={getToken} />}
          {tab === "summary" && <SummaryPanel apiBaseUrl={apiBaseUrl} getToken={getToken} />}
        </div>
      </div>
    </div>
  );
}

function LogsPanel({ apiBaseUrl, getToken }) {
  const [filters, setFilters] = useState({
    table_name: "",
    record_id: "",
    user_id: "",
    action: "",
    date_from: "",
    date_to: "",
    limit: 100,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const events = useMemo(() => groupLogRows(rows), [rows]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAuditLogs(apiBaseUrl, getToken, filters);
      setRows(data);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }

  }, [apiBaseUrl, getToken, filters]);

  useEffect(() => {
    fetchLogs();

  }, []);

  function updateFilter(key, value) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  function toggleExpanded(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div className="audit-layout">
      <aside className="audit-filters">
        <div className="filters-label">Filter</div>

        <label className="field">
          <span>Table</span>
          <input
            placeholder="e.g. suppliers"
            value={filters.table_name}
            onChange={(e) => updateFilter("table_name", e.target.value)}
          />
        </label>

        <label className="field">
          <span>Record ID</span>
          <input
            type="number"
            placeholder="e.g. 42"
            value={filters.record_id}
            onChange={(e) => updateFilter("record_id", e.target.value)}
          />
        </label>

        <label className="field">
          <span>Staff / user ID</span>
          <input
            type="number"
            placeholder="e.g. 7"
            value={filters.user_id}
            onChange={(e) => updateFilter("user_id", e.target.value)}
          />
        </label>

        <label className="field">
          <span>Action</span>
          <select value={filters.action} onChange={(e) => updateFilter("action", e.target.value)}>
            <option value="">Any</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
        </label>

        <label className="field">
          <span>From (IST)</span>
          <input
            type="datetime-local"
            value={filters.date_from}
            onChange={(e) => updateFilter("date_from", e.target.value)}
          />
        </label>

        <label className="field">
          <span>To (IST)</span>
          <input
            type="datetime-local"
            value={filters.date_to}
            onChange={(e) => updateFilter("date_to", e.target.value)}
          />
        </label>

        <label className="field">
          <span>Limit</span>
          <input
            type="number"
            min={1}
            max={1000}
            value={filters.limit}
            onChange={(e) => updateFilter("limit", e.target.value)}
          />
        </label>

        <button className="apply-btn" onClick={fetchLogs} disabled={loading}>
          <Search size={15} />
          {loading ? "Loading…" : "Apply filters"}
        </button>
      </aside>

      <main className="audit-main">
        <div className="main-toolbar">
          <span className="count">
            {loading ? "Loading…" : `${events.length} event${events.length === 1 ? "" : "s"}`}
          </span>
          <button className="icon-btn" onClick={fetchLogs} title="Refresh">
            <RefreshCw size={15} className={loading ? "spin" : ""} />
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className="empty-state">No changes match these filters.</div>
        )}

        <div className="ledger">
          {events.map((ev) => {
            const Icon = actionIcon(ev.action);
            const isOpen = expanded.has(ev.id);
            const summary = summarizeFields(ev.fields);
            return (
              <article
                key={ev.id}
                className="ledger-card"
                data-action={String(ev.action || "").toLowerCase()}
              >
                <div className="stamp" />
                <div className="ledger-body">
                  <button className="ledger-head" onClick={() => toggleExpanded(ev.id)}>
                    <span className="action-chip">
                      <Icon size={13} /> {actionLabel(ev.action)}
                    </span>
                    <span className="mono table-record">
                      {tableLabel(ev.table_name)} · {ev.record_name || `#${ev.record_id}`}
                    </span>
                    <span className="staff" title="Changed by">
                      {ev.staff_name || "System"}
                      {ev.staff_code ? ` (${ev.staff_code})` : ""}
                    </span>
                    {summary && <span className="summary-hint">{summary}</span>}
                    <span className="mono timestamp">{fmtTimestamp(ev.changed_at)}</span>
                    {ev.fields.length > 0 && (
                      <span className="chevron">
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                    )}
                  </button>

                  {isOpen && ev.fields.length > 0 && (
                    <div className="diff-table">
                      {ev.fields.map((f) => (
                        <div key={f.key} className="diff-row">
                          <span className="diff-field mono">{fieldLabel(f.key)}</span>
                          <span className="diff-old mono">
                            {f.old === null || f.old === undefined ? <em>null</em> : f.old}
                          </span>
                          <span className="diff-arrow">→</span>
                          <span className="diff-new mono">
                            {f.next === null || f.next === undefined ? <em>null</em> : f.next}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function DownloadsPanel({ apiBaseUrl, getToken }) {
  const [filters, setFilters] = useState({
    report_type: "",
    user_id: "",
    date_from: "",
    date_to: "",
    limit: 100,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchReportDownloads(apiBaseUrl, getToken, filters);
      setRows(data);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }

  }, [apiBaseUrl, getToken, filters]);

  useEffect(() => {
    fetchRows();

  }, []);

  return (
    <div className="audit-layout">
      <aside className="audit-filters">
        <div className="filters-label">Filter</div>
        <label className="field">
          <span>Report type</span>
          <input
            placeholder="e.g. vehicles"
            value={filters.report_type}
            onChange={(e) => setFilters((f) => ({ ...f, report_type: e.target.value }))}
          />
        </label>
        <label className="field">
          <span>Staff / user ID</span>
          <input
            type="number"
            value={filters.user_id}
            onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
          />
        </label>
        <label className="field">
          <span>From (IST)</span>
          <input
            type="datetime-local"
            value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          />
        </label>
        <label className="field">
          <span>To (IST)</span>
          <input
            type="datetime-local"
            value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          />
        </label>
        <button className="apply-btn" onClick={fetchRows} disabled={loading}>
          <Search size={15} /> {loading ? "Loading…" : "Apply filters"}
        </button>
      </aside>

      <main className="audit-main">
        <div className="main-toolbar">
          <span className="count">
            {loading ? "Loading…" : `${rows.length} download${rows.length === 1 ? "" : "s"}`}
          </span>
          <button className="icon-btn" onClick={fetchRows} title="Refresh">
            <RefreshCw size={15} className={loading ? "spin" : ""} />
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={16} /> <span>{error}</span>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="empty-state">No downloads match these filters.</div>
        )}

        {rows.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Type</th>
                <th>Staff</th>
                <th>Status</th>
                <th>Downloaded at</th>
                <th>Size</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.report_name}</td>
                  <td className="mono">{r.report_type}</td>
                  <td>
                    {r.staff_name || "—"}
                    {r.staff_code ? ` (${r.staff_code})` : ""}
                  </td>
                  <td>
                    <span className={`status-pill status-${r.status}`}>{r.status}</span>
                  </td>
                  <td className="mono">{fmtTimestamp(r.downloaded_at)}</td>
                  <td className="mono">
                    {r.file_size_bytes ? `${Math.round(r.file_size_bytes / 1024)} KB` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}

function SummaryPanel({ apiBaseUrl, getToken }) {
  const [filters, setFilters] = useState({ date_from: "", date_to: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDownloadSummary(apiBaseUrl, getToken, filters);
      setRows(data);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, getToken, filters]);

  useEffect(() => {
    fetchRows();

  }, []);

  const maxCount = Math.max(1, ...rows.map((r) => r.download_count || 0));

  return (
    <div className="audit-layout">
      <aside className="audit-filters">
        <div className="filters-label">Filter</div>
        <label className="field">
          <span>From (IST)</span>
          <input
            type="datetime-local"
            value={filters.date_from}
            onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          />
        </label>
        <label className="field">
          <span>To (IST)</span>
          <input
            type="datetime-local"
            value={filters.date_to}
            onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          />
        </label>
        <button className="apply-btn" onClick={fetchRows} disabled={loading}>
          <Search size={15} /> {loading ? "Loading…" : "Apply filters"}
        </button>
      </aside>

      <main className="audit-main">
        <div className="main-toolbar">
          <span className="count">
            {loading
              ? "Loading…"
              : `${rows.length} staff/report pairing${rows.length === 1 ? "" : "s"}`}
          </span>
          <button className="icon-btn" onClick={fetchRows} title="Refresh">
            <RefreshCw size={15} className={loading ? "spin" : ""} />
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <AlertCircle size={16} /> <span>{error}</span>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="empty-state">No successful downloads in this range.</div>
        )}

        <div className="summary-list">
          {rows.map((r, i) => (
            <div key={`${r.user_id}-${r.report_type}-${i}`} className="summary-row">
              <div className="summary-meta">
                <span className="summary-name">
                  {r.staff_name || "—"} {r.staff_code ? `(${r.staff_code})` : ""}
                </span>
                <span className="mono summary-type">{r.report_type}</span>
              </div>
              <div className="summary-bar-track">
                <div
                  className="summary-bar-fill"
                  style={{ width: `${(r.download_count / maxCount) * 100}%` }}
                />
              </div>
              <div className="summary-count mono">{r.download_count}</div>
              <div className="summary-last mono">{fmtTimestamp(r.last_downloaded_at)}</div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}